import React, { useCallback, useState } from 'react';
import { NodeHtmlMarkdown } from 'node-html-markdown';

import useToggle from '@lyearn/core/hooks/useToggle';
import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { useTranslation } from '@lyearn/i18n';
import {
  CheckmarkOutline,
  CopyOutline,
  SyncOutline,
  ThumbsDownFill,
  ThumbsDownOutline,
  ThumbsUpFill,
  ThumbsUpOutline,
} from '@lyearn/icons';
import { Button, IconButton, Stack, Tooltip, Typography, useSnackbar } from '@lyearn/ui';
import { ChatFragment } from '@/features/messages/graphql/fragments/generated/Chat';
import useReaction from '@/features/reactions/hooks/useReaction';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import { useCreateNoteMutation } from '@/modules/knowledge-base-copilot/graphql/mutations/generated/createNote';
import { NoteFragment } from '@/modules/knowledge-base-copilot/graphql/queries/generated/notes';
import { ChatbotMessageType } from '@/modules/knowledge-base-copilot/types';
import { Maybe } from '@/types/schema';

import { useDeletePostMutation } from '../../graphql/mutations/generated/deleteMessage';
import { useRegenerateGenerativeTaskMutation } from '../../graphql/mutations/generated/regenerateGenerativeTask';
import {
  extractH3Text,
  extractPlaintextFromHTML,
  removeLeadingH3,
  sanitizeHtmlForNote,
} from '../../utils';

const AIResponseFeedback = ({
  messageId,
  channel,
  textContent,
  allowRegeneration,
  setGenerativeTaskId,
  setMessages,
  setIsWaiting,
  setCurrentNote,
  generationHasFailed,
  teachModeEnabled,
}: {
  messageId: string;
  channel?: Maybe<ChatFragment>;
  textContent?: string;
  allowRegeneration: boolean;
  setGenerativeTaskId?: (taskId: string) => void;
  setMessages?: React.Dispatch<React.SetStateAction<ChatbotMessageType[]>>;
  setIsWaiting?: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentNote?: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
  generationHasFailed?: boolean;
  teachModeEnabled?: boolean;
}) => {
  const { t } = useTranslation(['AI', 'Common']);
  const { addReaction, removeReaction } = useReaction();
  const [liked, { toggle: toggleLiked }] = useToggle();
  const [disliked, { toggle: toggleDisliked }] = useToggle();
  const [isCopied, { toggle: toggleCopied, on: copiedOn }] = useToggle();
  const [, regenerateResponse] = useRegenerateGenerativeTaskMutation();
  const [, deleteMessage] = useDeletePostMutation();
  const { onCopy } = useCopyToClipboard();
  const [_, createNote] = useCreateNoteMutation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [disableRegeneration, setDisableRegeneration] = useState<boolean>(false);

  const channelId = channel?.id || '';

  const handleRegenerateResponse = () => {
    regenerateResponse({
      data: {
        generativeTaskId: messageId,
      },
    })
      .then((data) => {
        const taskId = data?.data?.regenerateGenerativeTask?.generativeTask?.id;
        if (taskId) {
          setMessages?.((prev: ChatbotMessageType[]) => prev.slice(0, -1));
          setIsWaiting?.(true);
          deleteMessage({
            data: {
              postId: messageId,
              channelId,
            },
          });
          setGenerativeTaskId?.(taskId);
        } else {
          setDisableRegeneration(true);
          ErrorReporter.error(
            new Error(
              `Didn't receive generativeTaskId from API during regeneration of response. generativeTaskId: <${messageId}> | API response: <${JSON.stringify(
                data,
              )}>`,
            ),
          );
        }
      })
      .catch((error) => {
        ErrorReporter.error(
          new Error(
            `Error regenerating response. generativeTaskId: <${messageId}> | error: <${error}>`,
          ),
        );
      });
  };

  const failedTaskGenerativeTaskId = messageId.includes('error-')
    ? messageId.split('-')[1]
    : undefined;

  const handleReaction = (isPositive: boolean, currentState: boolean) => {
    const reactionToAdd = isPositive ? '+1' : '-1';
    const reactionToRemove = isPositive ? '-1' : '+1';
    const toggleFn = isPositive ? toggleLiked : toggleDisliked;
    const oppositeToggleFn = isPositive ? toggleDisliked : toggleLiked;
    const oppositeState = isPositive ? disliked : liked;

    if (!currentState) {
      toggleFn();
      addReaction({
        reaction: {
          channelId,
          postId: messageId,
          reaction: reactionToAdd,
        },
      });

      if (oppositeState) {
        oppositeToggleFn();
        removeReaction({
          reaction: {
            channelId,
            postId: messageId,
            reaction: reactionToRemove,
          },
        });
      }
    } else {
      toggleFn();
      removeReaction({
        reaction: {
          channelId,
          postId: messageId,
          reaction: reactionToAdd,
        },
      });
    }
  };

  const feedbackButtons = [
    {
      icon: liked ? ThumbsUpFill : ThumbsUpOutline,
      onClick: () => handleReaction(true, liked),
      enabled: !generationHasFailed,
      title: t('Common:KnowledgeBase.GoodResponse'),
    },
    {
      icon: disliked ? ThumbsDownFill : ThumbsDownOutline,
      onClick: () => handleReaction(false, disliked),
      enabled: !generationHasFailed,
      title: t('Common:KnowledgeBase.BadResponse'),
    },
    {
      icon: isCopied ? CheckmarkOutline : CopyOutline,
      onClick: () => {
        if (isCopied) {
          return;
        }
        onCopy(NodeHtmlMarkdown.translate(textContent || ''));
        toggleCopied();
        setTimeout(() => {
          toggleCopied();
        }, 3000);
      },
      enabled: !generationHasFailed,
      title: t('Common:KnowledgeBase.Copy'),
    },
    {
      icon: SyncOutline,
      onClick: handleRegenerateResponse,
      enabled:
        allowRegeneration &&
        !disableRegeneration &&
        Boolean(setGenerativeTaskId) &&
        Boolean(setMessages) &&
        // generation has failed but task id isn't found => can't regenerate
        !(!failedTaskGenerativeTaskId && generationHasFailed),
      title: t('Common:KnowledgeBase.RegenerateResponse'),
    },
  ];

  const handleSaveToNotes = useCallback(() => {
    const title =
      extractH3Text(textContent) || channel?.name || t('Common:KnowledgeBase.UntitledNote');
    // Normalise the rich answer HTML so the note's HTML→blocks conversion keeps
    // the body (KaTeX/callouts/mentions otherwise yield an empty note).
    const htmlContent = sanitizeHtmlForNote(removeLeadingH3(textContent));
    const plainText = extractPlaintextFromHTML(htmlContent);

    createNote({
      input: {
        title,
        htmlContent,
        plainText,
      },
    })
      .then((res) => {
        if (res?.data?.createNote?.note?.id?.length) {
          showSnackbar({ message: t('Common:KnowledgeBase.NoteCreated') });
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
        }
      })
      .catch(() => {
        showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
      });
  }, [createNote, textContent, channel, showSnackbar, showErrorSnackbar, t]);

  return (
    <Stack
      alignItems="center"
      className="mt-8 -ms-4"
      justifyContent={generationHasFailed ? 'end' : 'between'}>
      <Stack alignItems="center" gap={4}>
        {getEnabledItems(feedbackButtons).map((button: any, index) => (
          <Tooltip key={index} placement="bottom" title={button.title}>
            <IconButton onClick={button.onClick}>
              <button.icon height={20} width={20} />
            </IconButton>
          </Tooltip>
        ))}
      </Stack>
      {generationHasFailed || teachModeEnabled ? null : (
        <Button
          appearance="secondary"
          className="ms-8"
          shape="pill"
          size="x-small"
          variant="outlined"
          onClick={handleSaveToNotes}>
          <Typography color="text-text-secondary" variant="body-short-02">
            {t('Common:KnowledgeBase.SaveToNotes')}
          </Typography>
        </Button>
      )}
    </Stack>
  );
};

export default AIResponseFeedback;
